import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArcaPageShell from '../components/ArcaPageShell';
import { useAuth } from '../../../contexts/AuthContext';
import { apiFetch } from '../../../api/client';
import { withUnidade } from '../../../utils/storageByUnit';
import { paJson } from './paApi';

function labelPrestador(p) {
  if (!p) return '';
  const num = p.numeroContrato || '';
  const emp = p.empresa || '';
  return num && emp ? `${num} — ${emp}` : emp || num || '';
}

export default function ArcaProcessoAdministrativoListPage() {
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tipo, setTipo] = useState('contratacao');
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [objeto, setObjeto] = useState('');
  const [contrato, setContrato] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [prestadorSel, setPrestadorSel] = useState(null);
  const [prestadores, setPrestadores] = useState([]);
  const [loadingPrest, setLoadingPrest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await paJson('/api/arca/processo-administrativo', unidadeSelecionada);
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingPrest(true);
      try {
        const res = await apiFetch(withUnidade('/api/prestadores', unidadeSelecionada));
        const json = await res.json().catch(() => []);
        if (!cancelled) setPrestadores(Array.isArray(json) ? json : []);
      } catch {
        if (!cancelled) setPrestadores([]);
      } finally {
        if (!cancelled) setLoadingPrest(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, unidadeSelecionada]);

  const options = useMemo(
    () =>
      (prestadores || [])
        .filter((p) => p && (p.empresa || p.numeroContrato))
        .map((p) => ({
          ...p,
          label: labelPrestador(p),
        }))
        .sort((a, b) => String(a.empresa || '').localeCompare(String(b.empresa || ''), 'pt-BR')),
    [prestadores]
  );

  const aplicarPrestador = (p) => {
    setPrestadorSel(p);
    if (!p) {
      setContrato('');
      setEmpresa('');
      return;
    }
    setContrato(p.numeroContrato || '');
    setEmpresa(p.empresa || '');
    const obj = p.objeto || p.objetoContrato || p.objeto_contrato;
    if (obj && !String(objeto || '').trim()) setObjeto(String(obj));
  };

  const resetForm = () => {
    setTipo('contratacao');
    setNumeroProcesso('');
    setObjeto('');
    setContrato('');
    setEmpresa('');
    setPrestadorSel(null);
  };

  const criar = async () => {
    setSaving(true);
    try {
      const created = await paJson('/api/arca/processo-administrativo', unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify({
          tipo,
          numeroProcesso,
          objetoContrato: objeto,
          contratoVinculado: tipo === 'aditivo' ? contrato : contrato || null,
          contratoEmpresa: empresa || null,
        }),
      });
      setOpen(false);
      resetForm();
      navigate(`/arca/pdf/processo-administrativo/${created.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ArcaPageShell
      title="Processo Administrativo"
      subtitle="Contratação e aditivo — checklist documental, pareceres e minuta"
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          Novo processo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {items.map((it) => (
            <ListItemButton
              key={it.id}
              sx={{ borderRadius: 2, border: '1px solid rgba(25,118,210,0.12)', mb: 1 }}
              onClick={() => navigate(`/arca/pdf/processo-administrativo/${it.id}`)}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography fontWeight={700}>
                      {it.numeroProcesso || `PA #${it.id}`}
                    </Typography>
                    <Chip size="small" label={it.tipo === 'aditivo' ? 'Aditivo' : 'Contratação'} />
                    <Chip size="small" variant="outlined" label={it.status} />
                    <Chip size="small" variant="outlined" label={it.origem} />
                  </Box>
                }
                secondary={`${it.contratoEmpresa || it.contratoVinculado || '—'} · ${it.objetoContrato || ''}`}
              />
            </ListItemButton>
          ))}
          {!items.length && <Alert severity="info">Nenhum processo administrativo nesta unidade.</Alert>}
        </List>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Processo Administrativo</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <MenuItem value="contratacao">Contratação</MenuItem>
              <MenuItem value="aditivo">Aditivo</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Nº do processo"
            fullWidth
            value={numeroProcesso}
            onChange={(e) => setNumeroProcesso(e.target.value)}
            placeholder="070/2026"
          />
          <Autocomplete
            sx={{ mt: 1 }}
            options={options}
            loading={loadingPrest}
            value={prestadorSel}
            onChange={(_, v) => aplicarPrestador(v)}
            getOptionLabel={(o) => o.label || labelPrestador(o)}
            isOptionEqualToValue={(a, b) =>
              String(a?.id || a?.numeroContrato) === String(b?.id || b?.numeroContrato)
            }
            filterOptions={(opts, { inputValue }) => {
              const q = String(inputValue || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/\p{M}/gu, '');
              if (!q) return opts;
              return opts.filter((o) => {
                const hay = `${o.empresa || ''} ${o.numeroContrato || ''} ${o.objeto || ''}`
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/\p{M}/gu, '');
                return hay.includes(q);
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id || option.numeroContrato}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.empresa}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Contrato {option.numeroContrato}
                    {option.setor ? ` · ${option.setor}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={tipo === 'aditivo' ? 'Empresa / contrato vinculado *' : 'Empresa (prestadores)'}
                placeholder="Digite o nome da empresa ou nº do contrato"
                helperText="Busca nos prestadores do módulo de Contratos (unidade atual)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPrest ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={loadingPrest ? 'Carregando…' : 'Nenhum prestador encontrado'}
          />
          {tipo === 'aditivo' && (
            <TextField
              margin="dense"
              label="Nº contrato vinculado *"
              fullWidth
              value={contrato}
              onChange={(e) => setContrato(e.target.value)}
              placeholder="Preenchido ao selecionar o prestador"
              helperText="Pode ajustar manualmente se necessário"
            />
          )}
          <TextField
            margin="dense"
            label="Empresa"
            fullWidth
            value={empresa}
            onChange={(e) => {
              setEmpresa(e.target.value);
              setPrestadorSel(null);
            }}
            helperText="Preenchida pelo dropdown; editável se precisar"
          />
          <TextField
            margin="dense"
            label="Objeto"
            fullWidth
            multiline
            minRows={2}
            value={objeto}
            onChange={(e) => setObjeto(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={saving || (tipo === 'aditivo' && !contrato.trim())}
            onClick={criar}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </ArcaPageShell>
  );
}
